import React, { useEffect, useState, Suspense } from "react";
import { Link, useParams } from "react-router-dom";

import { useObjectStore } from "../zustand/objects";
import { getSpecificUserProjectGuestAccessAPI } from "../api";
import AppNavBar from "../components/AppNavBar";
import StoryboardTextField from "../components/StoryboardTextField";
import { LazyStoryboardSceneThumb } from "../components/StoryboardSceneThumbPreview";

import {
  Box,
  Card,
  Button,
  Group,
  Text,
  Title,
  Container,
  Tooltip,
  Stack,
  SimpleGrid,
} from "@mantine/core";

import {
  editorModalShell,
  getStoryboardChromeTheme,
  storyboardCardMutedContentSectionStyle,
  storyboardCardRootStyleFromTheme,
  storyboardCardThumbStyleFromTheme,
  storyboardSceneButtonStyles,
  pageLayoutProps,
} from "../styleFile";
import { CHROME_HEADER_HEIGHT } from "../utils/uiChromeTokens";

export default function PreviewStoryboardPage() {
  const {
    currentObjectArtboard,
    updateArtboardAndPreviewCamera,
    getAIModels,
    objectsAreLoaded,
    artboards,
    getSpecificUserProjectGuest,
    projectName,
    loadObjects,
    unloadObjects,
    viewportThemeOverride,
  } = useObjectStore();

  const isDarkChrome = viewportThemeOverride === "dark";
  const sbTheme = getStoryboardChromeTheme(isDarkChrome);

  const { projectKey } = useParams();
  const [guest, setGuest] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.setAttribute("data-editor-theme", isDarkChrome ? "dark" : "light");
    return () => {
      document.body.removeAttribute("data-editor-theme");
    };
  }, [isDarkChrome]);

  useEffect(() => {
    unloadObjects();
    getSpecificUserProjectGuestAccessAPI(projectKey).then((response) => {
      if (response === true) {
        return getSpecificUserProjectGuest(projectKey)
          .then(() => setGuest(true))
          .then(() => getAIModels())
          .then(() => loadObjects())
          .then(() => updateArtboardAndPreviewCamera(1));
      }

      setGuest(false);
      return Promise.resolve();
    });
  }, [
    getAIModels,
    getSpecificUserProjectGuest,
    loadObjects,
    projectKey,
    unloadObjects,
    updateArtboardAndPreviewCamera,
  ]);

  const pageTitle = guest ? "View prototype storyboard" : "This project is private";

  return (
    <Stack gap={0} style={{ minHeight: "100vh", width: "100%", backgroundColor: sbTheme.stackBg }}>
      <AppNavBar pageName={projectName} />

      <Box
        style={{
          backgroundColor: sbTheme.mainBg,
          minHeight: `calc(100vh - ${CHROME_HEADER_HEIGHT}px)`,
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowY: "auto",
          paddingBottom: 24,
        }}
      >
        <Suspense
          fallback={<img src="/image/loading.gif" alt="" style={{ width: "50px", height: "50px" }} />}
        >
          <Container {...pageLayoutProps}>
            <Stack gap="lg">
              <Stack gap="md">
                <Box>
                  <Text
                    style={{
                      ...editorModalShell.styles.title,
                      color: sbTheme.sectionLabelColor,
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    Storyboard
                  </Text>
                  <Group justify="space-between" align="flex-end" wrap="wrap" gap="md" mt={6}>
                    <Title
                      order={2}
                      fw={600}
                      style={{
                        color: sbTheme.headlineColor,
                        fontSize: "1.125rem",
                        lineHeight: 1.35,
                        letterSpacing: "-0.02em",
                        flex: "1 1 240px",
                      }}
                    >
                      {pageTitle}
                    </Title>
                    {guest && (
                      <Button
                        styles={storyboardSceneButtonStyles}
                        component={Link}
                        to={`/share/${projectKey}?artboard=${currentObjectArtboard}`}
                        onClick={() => unloadObjects()}
                      >
                        View preview
                      </Button>
                    )}
                  </Group>
                </Box>
              </Stack>

              {guest && (
                <SimpleGrid cols={{ base: 2, md: 3, lg: 4, xl: 5 }} spacing="sm" verticalSpacing="sm">
                  {objectsAreLoaded &&
                    artboards &&
                    Object.keys(artboards).map((mapped) => (
                      <Card
                        key={mapped}
                        p={0}
                        radius={editorModalShell.radius}
                        withBorder={false}
                        shadow="sm"
                        styles={{
                          root: {
                            ...storyboardCardRootStyleFromTheme(sbTheme),
                            "&:hover": {
                              boxShadow: sbTheme.hoverShadow,
                            },
                          },
                        }}
                      >
                        <Card.Section p={0}>
                          <Box
                            style={{
                              position: "relative",
                              ...storyboardCardThumbStyleFromTheme(sbTheme),
                              backgroundImage: sbTheme.thumbPlaceholderGradient,
                            }}
                          >
                            <LazyStoryboardSceneThumb
                              sceneKey={mapped}
                              previewKey={`${projectKey}-${mapped}`}
                              objectsAreLoaded={objectsAreLoaded}
                              artboards={artboards}
                            />
                          </Box>
                        </Card.Section>
                        <Box style={storyboardCardMutedContentSectionStyle(sbTheme)}>
                          <Stack gap="sm" px="md" pt="md" pb={6}>
                            <Text
                              fw={600}
                              fz={13}
                              c={sbTheme.titleColor}
                              style={{
                                lineHeight: 1.3,
                              }}
                            >
                              Scene: {mapped}
                            </Text>
                            <Tooltip
                              label="Scene info is edited in the storyboard editor."
                              position="bottom-start"
                            >
                              <span>
                                <StoryboardTextField fieldType="name" isEditable={false} artboard={mapped} compact />
                              </span>
                            </Tooltip>
                            <Tooltip
                              label="Scene info is edited in the storyboard editor."
                              position="bottom-start"
                            >
                              <span>
                                <StoryboardTextField
                                  fieldType="description"
                                  isEditable={false}
                                  artboard={mapped}
                                  compact
                                />
                              </span>
                            </Tooltip>
                            <StoryboardTextField fieldType="comments" isEditable artboard={mapped} compact />
                          </Stack>
                          <Group gap="sm" grow px="md" pb="md" pt={4}>
                            <Button
                              size="xs"
                              styles={storyboardSceneButtonStyles}
                              component="a"
                              href={`/share/${projectKey}?artboard=${mapped}`}
                              onClick={() => unloadObjects()}
                            >
                              View
                            </Button>
                          </Group>
                        </Box>
                      </Card>
                    ))}
                </SimpleGrid>
              )}
            </Stack>
          </Container>
        </Suspense>
      </Box>
    </Stack>
  );
}
